import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import ConfirmationDialog from '@sayyesschool/frontend/components/confirmation-dialog';
import FormDialog from '@sayyesschool/frontend/components/form-dialog';
import LoadingIndicator from '@sayyesschool/frontend/components/loading-indicator';
import Page from '@sayyesschool/frontend/components/page';
import PasswordForm from '@sayyesschool/frontend/components/password-form';
import { HH_URL } from '@sayyesschool/frontend/constants';
import { useLearner } from '@sayyesschool/frontend/features/learners';
import { useUser } from '@sayyesschool/frontend/features/user';
import { useBoolean } from '@sayyesschool/frontend/hooks/state';
import { Flex, Grid } from '@sayyesschool/frontend/ui-components';

import LearnerContacts from '@/components/learners/learner-contacts';
import LearnerDetails from '@/components/learners/learner-details';
import LearnerEnrollments from '@/components/learners/learner-enrollments';
import LearnerForm from '@/components/learners/learner-form';
import LearnerMeetings from '@/components/learners/learner-meetings';
import LearnerMemberships from '@/components/learners/learner-memberships';
import LearnerPayments from '@/components/learners/learner-payments';
import LearnerRequests from '@/components/learners/learner-requests';
import LearnerTasks from '@/components/learners/learner-tasks';
import LearnerTransactions from '@/components/learners/learner-transactions';

export default function LearnerPage() {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [learner, learnerActions] = useLearner(id);
    const [user] = useUser();

    const [isLearnerFormOpen, toggleLearnerFormOpen] = useBoolean(false);
    const [isPasswordDialogOpen, togglePasswordDialogOpen] = useBoolean(false);
    const [isConfirmationDialogOpen, toggleConfirmationDialogOpen] = useBoolean(false);

    useEffect(() => {
        learnerActions.getLearner(id)
            .then(() => {
                if (location.state?.edit) {
                    toggleLearnerFormOpen(true);
                } else if (location.state?.delete) {
                    toggleConfirmationDialogOpen(true);
                }
            });

        return () => learnerActions.unsetLearner();
    }, [
        id,
        location.state,
        learnerActions,
        toggleLearnerFormOpen,
        toggleConfirmationDialogOpen
    ]);

    const updateLearner = useCallback(data => {
        return learnerActions.updateLearner(learner.id, data)
            .finally(() => toggleLearnerFormOpen(false));
    }, [learner, learnerActions, toggleLearnerFormOpen]);

    const updatePassword = useCallback(password => {
        return learnerActions.updateLearner(learner.id, { password })
            .finally(() => togglePasswordDialogOpen(false));
    }, [learner, learnerActions, togglePasswordDialogOpen]);

    const deleteLearner = useCallback(() => {
        return learnerActions.deleteLearner(learner.id)
            .then(() => navigate('/learners'));
    }, [learner, learnerActions, navigate]);

    if (!learner) return <LoadingIndicator />;

    return (
        <Page className="LearnerPage">
            <Page.Header
                breadcrumbs={[
                    { key: 'learners', content: 'Ученики', to: '/learners' }
                ]}
                title={learner?.fullname}
                description={`Баланс: ${learner?.balance} руб.`}
                actions={[
                    (learner.clientId && {
                        as: 'a',
                        href: `${HH_URL}/Profile/${learner.clientId}`,
                        target: '_blank',
                        icon: 'link',
                        title: 'Открыть в Hollihop'
                    }),
                    {
                        key: 'password',
                        title: 'Изменить пароль',
                        icon: 'password',
                        onClick: togglePasswordDialogOpen
                    },
                    {
                        key: 'edit',
                        title: 'Редактировать',
                        icon: 'edit',
                        onClick: toggleLearnerFormOpen
                    },
                    {
                        key: 'delete',
                        title: 'Удалить',
                        icon: 'delete',
                        onClick: toggleConfirmationDialogOpen
                    }
                ]}
            />

            <Page.Content>
                <Grid>
                    <Grid.Item
                        xs={12}
                        sm={8}
                        lg={9}
                    >
                        <Flex
                            gap="medium"
                            column
                        >
                            <LearnerRequests learner={learner} />

                            <LearnerEnrollments
                                learner={learner}
                                manager={{
                                    id: user.id
                                }}
                            />

                            <LearnerMemberships
                                learner={learner}
                                memberships={learner?.memberships}
                            />

                            <LearnerPayments learner={learner} />
                            <LearnerTransactions learner={learner} />
                        </Flex>
                    </Grid.Item>

                    <Grid.Item
                        xs={12}
                        sm={4}
                        lg={3}
                    >
                        <Flex
                            gap="medium"
                            column
                        >
                            <LearnerDetails learner={learner} />

                            <LearnerContacts
                                learner={learner}
                                onUpdate={updateLearner}
                            />
                        </Flex>
                    </Grid.Item>

                    <Grid.Item xs={12}>
                        <Flex
                            gap="medium"
                            column
                        >
                            <LearnerMeetings learner={learner} />
                            <LearnerTasks learner={learner} />
                        </Flex>
                    </Grid.Item>
                </Grid>
            </Page.Content>

            <FormDialog
                form="learner-form"
                title="Данные ученика"
                open={isLearnerFormOpen}
                onClose={toggleLearnerFormOpen}
            >
                <LearnerForm
                    id="learner-form"
                    learner={learner}
                    onSubmit={updateLearner}
                />
            </FormDialog>

            <FormDialog
                form="password-form"
                title="Изменение пароля"
                open={isPasswordDialogOpen}
                onClose={togglePasswordDialogOpen}
            >
                <PasswordForm
                    id="password-form"
                    onSubmit={updatePassword}
                />
            </FormDialog>

            <ConfirmationDialog
                title="Подтвердите действие"
                message="Вы действительно хотите удалить ученика?"
                open={isConfirmationDialogOpen}
                onConfirm={deleteLearner}
                onClose={toggleConfirmationDialogOpen}
            />
        </Page >
    );
}